import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetCommissionLedgerQuery, useResetCommissionMutation } from '../services/ledger/ledgerApi';
import Loading from '../components/Loading';




// Empty State Component
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-8 text-gray-400">
    <svg className="w-16 h-10 mb-2" viewBox="0 0 64 41" fill="none">
      <ellipse cx="32" cy="33" rx="32" ry="7" fill="#f5f5f5" />
      <path
        d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"
        fill="#fafafa"
        stroke="#d9d9d9"
      />
      <path
        d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"
        fill="#fafafa"
        stroke="#d9d9d9"
      />
    </svg>
    <span>No data</span>
  </div>
);

// Format date for API (YYYY-MM-DD)
const formatApiDate = function(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return year + '-' + month + '-' + day;
};

// Get default dates (1 month ago to today)
const getDefaultDates = function() {
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  
  return {
    fromDate: formatApiDate(oneMonthAgo),
    toDate: formatApiDate(today),
  };
};


const CommissionLenDenPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useSelector(function(state) { return state.auth; });
  const defaultDates = getDefaultDates();

  // Read userId from URL query params (e.g. /ledger/comm-lenden?userId=abc123)
  const urlUserId = new URLSearchParams(location.search).get('userId') || undefined;

  // State
  const [fromDate, setFromDate] = useState(defaultDates.fromDate);
  const [toDate, setToDate] = useState(defaultDates.toDate);
  const [page, setPage] = useState(1);
  const limit = 50;
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, clientId: null, clientName: '' });

  // Build query params
  const queryParams = {
    startDate: fromDate,
    endDate: toDate,
    page,
    limit
  };

  if (urlUserId) {
    queryParams.userId = urlUserId;
  }
  
  // RTK Query hook - automatically fetches on mount
  const { data: response, isLoading, error: apiError, refetch } = useGetCommissionLedgerQuery(queryParams);
  const [resetCommission] = useResetCommissionMutation();
  
  const clients = response?.data?.clients || [];
  const pagination = response?.data?.pagination || { page: 1, limit: 50, total: 0, totalPages: 1 };
  const totals = response?.data?.totals || { 
    milaMComm: 0, 
    milaSComm: 0, 
    milaMTK: 0, 
    milaTotal: 0,
    denaMComm: 0,
    denaSComm: 0,
    denaMTK: 0,
    denaTotal: 0
  };
  // Handle reset commission
  const handleReset = async () => {
    try {
      await resetCommission(confirmModal.clientId).unwrap();
      refetch();
    } catch {
      return '';
    } finally {
      setConfirmModal({ isOpen: false, clientId: null, clientName: '' });
    }
  };

  const filteredClients = clients;

  return (
    <div className="w-full bg-gray-100 min-h-screen">
      <style>{`
        .comm-mila-main  { background: #16a34a !important; color: #fff !important; }
        .comm-dena-main  { background: #dc2626 !important; color: #fff !important; }
        .comm-dark-th    { background: #2c3e50 !important; color: #fff !important; }
      `}</style>
      <div className="w-full">
        <div className="bg-white">
          {/* Header */}
          <div className="bg-[#2c3e50] px-5 py-3 flex items-center justify-between">
            <span className="text-xl text-white font-normal">
              Client Commission Lenden
            </span>
            <button 
              onClick={function() { navigate(-1); }}
              className="bg-blue-500 text-white px-6 py-1.5 hover:bg-blue-600 text-sm"
            >
              Back
            </button>
          </div>

          {/* Filters Row */}
          <div className="px-5 py-4 flex items-center gap-4 bg-white border-b">
            {/* Date Range */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={fromDate}
                onChange={function(e) { setFromDate(e.target.value); }}
                className="px-3 py-1.5 border border-gray-300 text-sm"
              />
              <input
                type="date"
                value={toDate}
                onChange={function(e) { setToDate(e.target.value); }}
                className="px-3 py-1.5 border border-gray-300 text-sm"
              />
            </div>

            {/* Client Filter Dropdown */}
            {/* <div className="relative">
              <select
                value={selectedClient}
                onChange={function(e) { setSelectedClient(e.target.value); }}
                className="px-4 py-1.5 border border-gray-300 bg-white text-sm min-w-[150px]"
              >
                {uniqueClients.map(client => (
                  <option key={client} value={client}>
                    {client === 'all' ? 'All' : client}
                  </option>
                ))}
              </select>
            </div> */}
          </div>

          {/* Error Message */}
          {apiError && (
            <div className="px-5 py-3 bg-red-50">
              <div className="text-red-600 text-sm">{apiError?.data?.message || 'Failed to fetch commission report'}</div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loading />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  {/* Main Header Row */}
                  <tr className="text-white">
                    <th rowSpan="2" className="comm-dark-th p-3 border border-gray-600 text-left font-medium">Name</th>
                    <th colSpan="5" className="comm-mila-main p-3 border border-gray-600 text-center font-bold">MILA HAI</th>
                    <th colSpan="5" className="comm-dena-main p-3 border border-gray-600 text-center font-bold">DENA HAI</th>
                  </tr>
                  {/* Sub Header Row */}
                  <tr>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">M.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">S.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">MTK.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">Casino</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">T.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">M.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">S.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">MTK.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">Casino</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">T.Comm</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {filteredClients.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="border border-gray-300 py-8">
                        <EmptyState />
                      </td>
                    </tr>
                  ) : (
                    <>
                      {filteredClients.map(function(client, index) {
                        const rowBgClass = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                        
                        return (
                          <tr key={client.clientId} className={rowBgClass}>
                            <td className="p-3 border border-gray-300">
                              <div className="flex flex-col gap-1">
                                <span
                                  className="text-blue-600 cursor-pointer hover:underline font-medium text-sm leading-tight"
                                  onClick={() => navigate(`/ledger/commission-details?clientId=${client.clientId}&clientName=${client.clientName}`)}
                                >
                                  {client.clientName}
                                </span>
                                <span className="text-gray-500 text-xs">{client.clientId}</span>
                                <div className="flex gap-1.5 mt-0.5">
                                  <button
                                    className="bg-blue-500 text-white px-3 py-0.5 text-xs hover:bg-blue-600 whitespace-nowrap"
                                    onClick={() => setConfirmModal({ isOpen: true, clientId: client.clientId, clientName: client.clientName })}
                                  >
                                    Reset
                                  </button>
                                  <button
                                    className="bg-green-500 text-white px-3 py-0.5 text-xs hover:bg-green-600 whitespace-nowrap"
                                    onClick={() => navigate(`/ledger/commission-details?clientId=${client.clientId}&clientName=${client.clientName}`)}
                                  >
                                    Detail
                                  </button>
                                </div>
                              </div>
                            </td>
                            {/* MILA HAI columns */}
                            <td className="p-3 border border-gray-300 text-right text-green-600">
                              {Number(client.milaMComm || 0).toFixed(1)}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-green-600">
                              {Number(client.milaSComm || 0).toFixed(0)}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-green-600">
                              {Number(client.milaMTK || 0).toFixed(0)}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-green-600">
                              {Number(client.milaCasino || 0).toFixed(0)}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-green-600 font-medium">
                              {Number(client.milaTotal || 0).toFixed(1)}
                            </td>
                            {/* DENA HAI columns */}
                            <td className="p-3 border border-gray-300 text-right text-red-600">
                              {Number(client.denaMComm || 0).toFixed(0)}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-red-600">
                              {Number(client.denaSComm || 0).toFixed(0)}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-red-600">
                              {Number(client.denaMTK || 0).toFixed(0)}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-red-600">
                              {Number(client.denaCasino || 0).toFixed(0)}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-red-600 font-medium">
                              {Number(client.denaTotal || 0).toFixed(0)}
                            </td>
                          </tr>
                        );
                      })}
                      
                      {/* Total Row */}
                      <tr className="bg-gray-100 font-semibold">
                        <td className="p-3 border border-gray-300">Total</td>
                        <td className="p-3 border border-gray-300 text-right text-green-600">
                          {Number(totals.milaMComm || 0).toFixed(1)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-green-600">
                          {Number(totals.milaSComm || 0).toFixed(0)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-green-600">
                          {Number(totals.milaMTK || 0).toFixed(0)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-green-600">
                          {Number(totals.milaCasino || 0).toFixed(0)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-green-600">
                          {Number(totals.milaTotal || 0).toFixed(1)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-red-600">
                          {Number(totals.denaMComm || 0).toFixed(0)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-red-600">
                          {Number(totals.denaSComm || 0).toFixed(0)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-red-600">
                          {Number(totals.denaMTK || 0).toFixed(0)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-red-600">
                          {Number(totals.denaCasino || 0).toFixed(0)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-red-600">
                          {Number(totals.denaTotal || 0).toFixed(0)}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {/* {!isLoading && pagination.totalPages > 1 && ( */}
            <div className="px-5 py-4 border-t border-gray-300 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className={`px-3 py-1 border ${
                    page === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <div className="flex gap-1">
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 border ${
                        pageNum === page
                          ? 'bg-blue-500 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className={`px-3 py-1 border ${
                    page === pagination.totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          {/* )} */}
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-2 text-gray-800">Reset Commission</h3>
            <p className="text-sm text-gray-600 mb-5">
              Kya aap <strong>{confirmModal.clientName}</strong> ki commission reset karna chahte hain?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmModal({ isOpen: false, clientId: null, clientName: '' })}
                className="px-4 py-2 text-sm border border-gray-300 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm bg-blue-500 text-white hover:bg-blue-600"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionLenDenPage;